import { useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

/**
 * A tab bar that is a real tab list: `role="tablist"`, each tab tied to its
 * panel by id, arrow keys (and Home and End) moving focus between them.
 *
 * **Manual activation** in WAI-ARIA's terms: the arrow moves focus and Enter,
 * Space or a click chooses. Automatic activation would be the smoother
 * pattern for tabs that are free, and these are not — choosing one mounts its
 * cards and every card is an audited read, so arrowing past four tabs would
 * write four rows to the log about screens nobody looked at.
 *
 * Controlled: the caller owns which tab is current, because on the business
 * page that is the address bar's to say (`router.ts`), and a tab bar that kept
 * its own copy would disagree with a refresh.
 *
 * `idPrefix` keeps two tab bars on one page from minting the same ids. The
 * panel is the caller's too — `TabPanel` below gives it the matching id and
 * role, so the pairing is written once.
 */
export interface TabItem<K extends string> {
  id: K;
  label: string;
  /** A small warning count beside the label — "Connections 2". */
  count?: number | undefined;
}

export function Tabs<K extends string>({
  label,
  idPrefix,
  tabs,
  current,
  onPick,
}: {
  label: string;
  idPrefix: string;
  tabs: readonly TabItem<K>[];
  current: K;
  onPick: (id: K) => void;
}): ReactNode {
  const refs = useRef(new Map<K, HTMLButtonElement>());

  const move = (e: KeyboardEvent<HTMLDivElement>): void => {
    const focused = tabs.findIndex((tab) => refs.current.get(tab.id) === document.activeElement);
    const at = focused === -1 ? tabs.findIndex((tab) => tab.id === current) : focused;
    let next = -1;
    if (e.key === 'ArrowRight') next = (at + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (at - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    const target = tabs[next];
    if (target === undefined) return;
    e.preventDefault();
    refs.current.get(target.id)?.focus();
  };

  return (
    <div className="tabs" role="tablist" aria-label={label} onKeyDown={move}>
      {tabs.map((tab) => {
        const selected = tab.id === current;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              if (node === null) refs.current.delete(tab.id);
              else refs.current.set(tab.id, node);
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            className="tabs__tab"
            onClick={() => onPick(tab.id)}
          >
            {tab.label}
            {tab.count === undefined || tab.count === 0 ? null : (
              <span className="tabs__count">{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  id,
  className,
  children,
}: {
  idPrefix: string;
  id: string;
  className?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${id}`}
      aria-labelledby={`${idPrefix}-tab-${id}`}
      className={className}
    >
      {children}
    </div>
  );
}
